import { Box, Group, Modal, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { RevisionGetLockStatusResponse } from 'api/actions/revision-get-lock-status/revision-get-lock-status-response';
import { RevisionLockResponse } from 'api/actions/revision-lock/revision-lock-response';
import { nanoid } from 'nanoid';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import P1Regular from 'components/typography/P1Regular';
import panic from 'errors/panic';
import { getForceTakeoverPermissionSlugForRevisionKind } from 'model/RevisionPermission';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ERROR_NOTIFICATION_COLOR, WARNING_NOTIFICATION_COLOR } from 'utils/constants';

const LOCK_DURATION_SECONDS = 60;
const HEARTBEAT_INTERVAL_MS = 15000;
const TAKEOVER_WAIT_INTERVAL_MS = 3000;

type RevisionLockStage =
  | 'initializing'
  | 'ready'
  | 'blocked-other-user'
  | 'blocked-other-session'
  | 'waiting-for-takeover'
  | 'being-taken-over';

type RevisionLockPollingMode = 'idle' | 'heartbeat' | 'takeover-wait';

interface IRevisionLockDebugInfo {
  revisionId: number;
  lockToken: string;
  stage: RevisionLockStage;
  lockState: string | null;
  takeoverState: string | null;
  isLocked: boolean;
  lockedByOther: boolean;
  isLockedByCurrentUser: boolean | null;
  isLockedByCurrentSession: boolean | null;
  lockedByUserName: string | null;
  lockedAt: string | null;
  expiresAt: string | null;
  lastActivityAt: string | null;
  takeoverRequestedAt: string | null;
  pollingMode: RevisionLockPollingMode;
  nextHeartbeatAt: number | null;
  nextTakeoverPollAt: number | null;
  heartbeatInFlight: boolean;
  takeoverWaitInFlight: boolean;
}

interface IRevisionLockProviderContext {
  isLocked: boolean;
  lockedByOther: boolean;
  lockToken: string;
  stage: RevisionLockStage;
  debugInfo: IRevisionLockDebugInfo;
  registerTakeoverSaveHandler: (handler: (() => Promise<void>) | null) => void;
}

const RevisionLockProviderContext = createContext<IRevisionLockProviderContext>(undefined!);

/**
 * Creates an ephemeral revision editor lock token for the current mounted tab session.
 */
function createLockToken() {
  return globalThis.crypto?.randomUUID?.() ?? nanoid();
}

/**
 * Provider that manages revision locking to prevent concurrent editing.
 */
export function RevisionLockProvider({
  children,
  revisionId,
  revisionKind,
  departmentId,
  redirectPath,
  onTakeoverRefetch,
}: {
  children: ReactNode;
  revisionId: number;
  revisionKind: string;
  departmentId: number;
  redirectPath: string;
  onTakeoverRefetch: () => Promise<unknown>;
}) {
  const { getAction } = useApi();
  const navigate = useNavigate();
  const [lockToken] = useState(createLockToken);
  const [stage, setStage] = useState<RevisionLockStage>('initializing');
  const [isLocked, setIsLocked] = useState(false);
  const [lockedByOther, setLockedByOther] = useState(false);
  const [lockedByUserName, setLockedByUserName] = useState<string>('');
  const [lockDebugStatus, setLockDebugStatus] = useState<RevisionGetLockStatusResponse | null>(null);
  const [pollingMode, setPollingMode] = useState<RevisionLockPollingMode>('idle');
  const [nextHeartbeatAt, setNextHeartbeatAt] = useState<number | null>(null);
  const [nextTakeoverPollAt, setNextTakeoverPollAt] = useState<number | null>(null);
  const [heartbeatInFlight, setHeartbeatInFlight] = useState(false);
  const [takeoverWaitInFlight, setTakeoverWaitInFlight] = useState(false);
  const [forceTakeoverInFlight, setForceTakeoverInFlight] = useState(false);
  const [canForceTakeover, setCanForceTakeover] = useState(false);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const takeoverWaitIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const takeoverSaveHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const heartbeatRequestInFlightRef = useRef(false);
  const takeoverWaitRequestInFlightRef = useRef(false);
  const isUnlockingRef = useRef(false);
  const isHandlingTakeoverRef = useRef(false);
  const isMountedRef = useRef(true);

  /**
   * Builds a display name for the user who currently owns the lock.
   */
  const getLockedByUserName = useCallback(
    (response: any) =>
      response.lockedBy?.firstName && response.lockedBy?.lastName
        ? `${response.lockedBy.firstName} ${response.lockedBy.lastName}`
        : response.lockedBy?.email || 'iný používateľ',
    []
  );

  /**
   * Stores the latest backend lock status snapshot for debug and UI diagnostics.
   */
  const updateDebugStatus = useCallback((response: RevisionGetLockStatusResponse) => {
    setLockDebugStatus(response);
  }, []);

  /**
   * Creates a current-session lock status snapshot from a successful acquire response.
   */
  const updateDebugStatusFromLock = useCallback((response: RevisionLockResponse) => {
    setLockDebugStatus({
      revisionId: response.revisionId,
      isLocked: true,
      lockState: 'lockedByCurrentSession',
      takeoverState: 'none',
      lockedAt: response.lockedAt,
      expiresAt: response.expiresAt,
      lastActivityAt: response.lastActivityAt,
      takeoverRequestedAt: undefined,
      isLockedByCurrentUser: true,
      isLockedByCurrentSession: true,
    });
  }, []);

  /**
   * Stops the periodic heartbeat requests.
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    heartbeatRequestInFlightRef.current = false;
    setHeartbeatInFlight(false);
    setNextHeartbeatAt(null);
  }, []);

  /**
   * Stops the polling loop that waits for takeover handoff completion.
   */
  const stopTakeoverWait = useCallback(() => {
    if (takeoverWaitIntervalRef.current) {
      clearInterval(takeoverWaitIntervalRef.current);
      takeoverWaitIntervalRef.current = null;
    }

    takeoverWaitRequestInFlightRef.current = false;
    setTakeoverWaitInFlight(false);
    setNextTakeoverPollAt(null);
  }, []);

  /**
   * Stops all active polling loops before switching to a different lock state.
   */
  const stopAllIntervals = useCallback(() => {
    stopHeartbeat();
    stopTakeoverWait();
    setPollingMode('idle');
  }, [stopHeartbeat, stopTakeoverWait]);

  /**
   * Applies a non-editable lock state to the UI.
   */
  const applyBlockedState = useCallback(
    (nextStage: Extract<RevisionLockStage, 'blocked-other-user' | 'blocked-other-session'>, response: any) => {
      stopAllIntervals();
      updateDebugStatus(response);
      setLockedByUserName(getLockedByUserName(response));
      setLockedByOther(true);
      setIsLocked(false);
      setStage(nextStage);
    },
    [getLockedByUserName, stopAllIntervals, updateDebugStatus]
  );

  /**
   * Fetches the latest lock status for the current revision and lock token.
   */
  const getLockStatus = useCallback(async () => {
    const action = getAction('RevisionGetLockStatus');

    return action({
      parameters: { revisionId: String(revisionId) },
      query: { lockToken },
    });
  }, [getAction, lockToken, revisionId]);

  /**
   * Attempts to acquire the revision lock for the current tab session.
   */
  const acquireLock = useCallback(
    async ({ activateEditing = true }: { activateEditing?: boolean } = {}) => {
      try {
        const action = getAction('RevisionLock');

        const response = await action({
          parameters: { revisionId: String(revisionId) },
          payload: {
            lockDurationSeconds: LOCK_DURATION_SECONDS,
            lockToken,
          },
        });

        updateDebugStatusFromLock(response);

        if (isMountedRef.current) {
          setIsLocked(true);
          setLockedByOther(false);

          if (activateEditing) {
            setStage('ready');
          }
        }

        return true;
      } catch (error: any) {
        if (!error?.response) {
          panic(error);
        }

        return false;
      }
    },
    [getAction, lockToken, revisionId, updateDebugStatusFromLock]
  );

  /**
   * Sends a heartbeat to keep the current lock alive.
   */
  const heartbeat = useCallback(async () => {
    const action = getAction('RevisionHeartbeat');

    return action({
      parameters: { revisionId: String(revisionId) },
      payload: {
        lockDurationSeconds: LOCK_DURATION_SECONDS,
        lockToken,
      },
    });
  }, [getAction, lockToken, revisionId]);

  /**
   * Requests takeover of the lock from another tab owned by the same user.
   */
  const requestTakeover = useCallback(async () => {
    const action = getAction('RevisionRequestTakeover');

    return action({
      parameters: { revisionId: String(revisionId) },
      payload: { lockToken },
    });
  }, [getAction, lockToken, revisionId]);

  /**
   * Forces immediate takeover of the lock from another tab owned by the same user.
   */
  const forceTakeover = useCallback(async () => {
    const action = getAction('RevisionForceTakeover');

    return action({
      parameters: { revisionId: String(revisionId) },
      payload: {
        lockDurationSeconds: LOCK_DURATION_SECONDS,
        lockToken,
      },
    });
  }, [getAction, lockToken, revisionId]);

  /**
   * Unlocks the revision for the current lock token.
   */
  const unlockRevision = useCallback(async () => {
    if (isUnlockingRef.current) {
      return;
    }

    isUnlockingRef.current = true;

    try {
      const action = getAction('RevisionUnlock');

      await action({
        parameters: { revisionId: String(revisionId) },
        payload: { lockToken },
      });
      setLockDebugStatus(null);
    } catch (error: any) {
      if (error?.response) {
        // eslint-disable-next-line no-console
        console.error('Failed to unlock revision:', error);
      } else {
        panic(error);
      }
    } finally {
      isUnlockingRef.current = false;
    }
  }, [getAction, lockToken, revisionId]);

  /**
   * Handles takeover detected by the current lock owner.
   */
  const handleIncomingTakeover = useCallback(async () => {
    if (isHandlingTakeoverRef.current) {
      return;
    }

    isHandlingTakeoverRef.current = true;
    stopAllIntervals();

    if (isMountedRef.current) {
      setStage('being-taken-over');
    }

    try {
      await takeoverSaveHandlerRef.current?.();
    } catch (error) {
      notifications.show({
        title: 'Nepodarilo sa uložiť posledné zmeny',
        message:
          'Táto revízia bola prevzatá v inom okne. Posledné automatické uloženie zlyhalo, ale úprava sa aj tak odovzdáva novému oknu.',
        color: ERROR_NOTIFICATION_COLOR,
      });
    } finally {
      await unlockRevision();

      if (isMountedRef.current) {
        setIsLocked(false);
        navigate(redirectPath, { replace: true });
      }
    }
  }, [navigate, redirectPath, stopAllIntervals, unlockRevision]);

  /**
   * Starts the periodic heartbeat loop once the current tab owns the lock.
   */
  const startHeartbeat = useCallback(() => {
    stopAllIntervals();
    setPollingMode('heartbeat');
    setNextHeartbeatAt(Date.now() + HEARTBEAT_INTERVAL_MS);

    heartbeatIntervalRef.current = setInterval(() => {
      if (heartbeatRequestInFlightRef.current) {
        return;
      }

      heartbeatRequestInFlightRef.current = true;
      setHeartbeatInFlight(true);
      setNextHeartbeatAt(Date.now() + HEARTBEAT_INTERVAL_MS);

      heartbeat()
        .then(async (response) => {
          updateDebugStatus(response);

          if (response.takeoverState === 'requestedFromCurrentSession') {
            handleIncomingTakeover().catch(panic);
            return;
          }

          if (response.lockState === 'lockedByCurrentSession') {
            return;
          }

          if (response.lockState === 'unlocked') {
            const acquired = await acquireLock();

            if (!acquired) {
              const retryStatus = await getLockStatus();

              if (retryStatus.lockState === 'lockedByCurrentUserOtherSession') {
                applyBlockedState('blocked-other-session', retryStatus);
              } else if (retryStatus.lockState === 'lockedByOtherUser') {
                applyBlockedState('blocked-other-user', retryStatus);
              }
            }
          }
        })
        .catch(panic)
        .finally(() => {
          heartbeatRequestInFlightRef.current = false;
          setHeartbeatInFlight(false);
        });
    }, HEARTBEAT_INTERVAL_MS);
  }, [
    acquireLock,
    applyBlockedState,
    getLockStatus,
    handleIncomingTakeover,
    heartbeat,
    stopAllIntervals,
    updateDebugStatus,
  ]);

  /**
   * Refreshes revision data after a successful takeover before editing resumes.
   */
  const completeTakeover = useCallback(async () => {
    if (isMountedRef.current) {
      setIsLocked(true);
      setLockedByOther(false);
    }

    await onTakeoverRefetch();

    if (isMountedRef.current) {
      setStage('ready');
    }

    startHeartbeat();
  }, [onTakeoverRefetch, startHeartbeat]);

  /**
   * Polls until the old lock owner releases the lock, then acquires it for this tab.
   */
  const waitForTakeoverCompletion = useCallback(async () => {
    const status = await getLockStatus();
    updateDebugStatus(status);

    if (status.lockState === 'lockedByCurrentSession') {
      await completeTakeover();
      stopTakeoverWait();
      return true;
    }

    if (status.lockState === 'unlocked') {
      const acquired = await acquireLock({ activateEditing: false });

      if (acquired) {
        await completeTakeover();
        stopTakeoverWait();
        return true;
      }

      return false;
    }

    if (status.lockState === 'lockedByOtherUser') {
      stopTakeoverWait();
      applyBlockedState('blocked-other-user', status);
      return true;
    }

    return false;
  }, [acquireLock, applyBlockedState, completeTakeover, getLockStatus, stopTakeoverWait, updateDebugStatus]);

  /**
   * Starts waiting for the old owner to release the lock after a takeover request.
   */
  const startTakeoverWait = useCallback(() => {
    stopAllIntervals();
    setPollingMode('takeover-wait');
    setNextTakeoverPollAt(Date.now() + TAKEOVER_WAIT_INTERVAL_MS);

    takeoverWaitIntervalRef.current = setInterval(() => {
      if (takeoverWaitRequestInFlightRef.current) {
        return;
      }

      takeoverWaitRequestInFlightRef.current = true;
      setTakeoverWaitInFlight(true);
      setNextTakeoverPollAt(Date.now() + TAKEOVER_WAIT_INTERVAL_MS);

      waitForTakeoverCompletion()
        .catch(panic)
        .finally(() => {
          takeoverWaitRequestInFlightRef.current = false;
          setTakeoverWaitInFlight(false);
        });
    }, TAKEOVER_WAIT_INTERVAL_MS);
  }, [stopAllIntervals, waitForTakeoverCompletion]);

  /**
   * Initializes the revision lock state for the current tab.
   */
  useEffect(() => {
    const action = getAction('AuthUserHasPermissionInDepartment');
    const permissionSlug = getForceTakeoverPermissionSlugForRevisionKind(revisionKind);
    let cancelled = false;

    setCanForceTakeover(false);

    action({
      parameters: {
        departmentId: String(departmentId),
        permissionSlug,
      },
    })
      .then(({ hasPermission }) => {
        if (!cancelled && isMountedRef.current) {
          setCanForceTakeover(hasPermission);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          panic(error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [departmentId, getAction, revisionKind]);

  useEffect(() => {
    isMountedRef.current = true;

    /**
     * Resolves the initial lock state for this tab and starts the appropriate flow.
     */
    const initialize = async () => {
      try {
        const status = await getLockStatus();
        updateDebugStatus(status);

        if (!isMountedRef.current) {
          return;
        }

        if (status.lockState === 'lockedByCurrentSession') {
          setIsLocked(true);
          setLockedByOther(false);
          setStage('ready');
          startHeartbeat();
          return;
        }

        if (status.lockState === 'unlocked') {
          const acquired = await acquireLock();

          if (!isMountedRef.current) {
            return;
          }

          if (acquired) {
            startHeartbeat();
            return;
          }

          const retryStatus = await getLockStatus();
          updateDebugStatus(retryStatus);

          if (!isMountedRef.current) {
            return;
          }

          if (retryStatus.lockState === 'lockedByCurrentUserOtherSession') {
            applyBlockedState('blocked-other-session', retryStatus);
          } else if (retryStatus.lockState === 'lockedByOtherUser') {
            applyBlockedState('blocked-other-user', retryStatus);
          }

          return;
        }

        if (status.lockState === 'lockedByCurrentUserOtherSession') {
          applyBlockedState('blocked-other-session', status);
          return;
        }

        applyBlockedState('blocked-other-user', status);
      } catch (error: any) {
        panic(error);
      }
    };

    initialize();

    return () => {
      isMountedRef.current = false;
      stopAllIntervals();
    };
  }, [acquireLock, applyBlockedState, getLockStatus, startHeartbeat, stopAllIntervals, updateDebugStatus]);

  /**
   * Releases the lock as best-effort cleanup when the provider unmounts.
   */
  useEffect(() => {
    return () => {
      if (isLocked) {
        unlockRevision().catch(panic);
      }
    };
  }, [isLocked, unlockRevision]);

  /**
   * Registers the final-save callback used during takeover handoff.
   */
  const registerTakeoverSaveHandler = useCallback((handler: (() => Promise<void>) | null) => {
    takeoverSaveHandlerRef.current = handler;
  }, []);

  /**
   * Handles explicit takeover request from the blocked same-user session.
   */
  const handleTakeoverClick = async () => {
    stopAllIntervals();
    setStage('waiting-for-takeover');

    try {
      const response = await requestTakeover();
      updateDebugStatus(response);

      if (response.lockState === 'unlocked') {
        const acquired = await acquireLock({ activateEditing: false });

        if (acquired) {
          await completeTakeover();
          return;
        }
      }

      const completedImmediately = await waitForTakeoverCompletion();

      if (!completedImmediately) {
        startTakeoverWait();
      }
    } catch (error: any) {
      setStage('blocked-other-session');
      panic(error);
    }
  };

  /**
   * Forces immediate takeover from the waiting same-user session.
   */
  const handleForceTakeoverClick = async () => {
    stopAllIntervals();
    setForceTakeoverInFlight(true);

    try {
      const response = await forceTakeover();
      updateDebugStatus(response);

      notifications.show({
        title: 'Prevzatie bolo vynútené',
        message:
          'Revízia bola okamžite prevzatá v tomto okne. Neuložené zmeny z pôvodného okna sa mohli stratiť alebo zostať nekonzistentné.',
        color: WARNING_NOTIFICATION_COLOR,
      });

      if (response.lockState === 'lockedByCurrentSession') {
        await completeTakeover();
        return;
      }

      if (response.lockState === 'unlocked') {
        const acquired = await acquireLock({ activateEditing: false });

        if (acquired) {
          await completeTakeover();
          return;
        }
      }

      if (response.lockState === 'lockedByOtherUser') {
        applyBlockedState('blocked-other-user', response);
        return;
      }

      applyBlockedState('blocked-other-session', response);
    } catch (error: any) {
      setStage('blocked-other-session');
      panic(error);
    } finally {
      setForceTakeoverInFlight(false);
    }
  };

  /**
   * Returns to the previous page after acknowledging a blocking modal.
   */
  const handleAcknowledge = () => {
    stopAllIntervals();
    navigate(-1);
  };

  const value = useMemo(
    () => ({
      isLocked,
      lockedByOther,
      lockToken,
      stage,
      debugInfo: {
        revisionId,
        lockToken,
        stage,
        lockState: lockDebugStatus?.lockState ?? null,
        takeoverState: lockDebugStatus?.takeoverState ?? null,
        isLocked,
        lockedByOther,
        isLockedByCurrentUser: lockDebugStatus?.isLockedByCurrentUser ?? null,
        isLockedByCurrentSession: lockDebugStatus?.isLockedByCurrentSession ?? null,
        lockedByUserName: lockDebugStatus ? getLockedByUserName(lockDebugStatus) : lockedByUserName || null,
        lockedAt: lockDebugStatus?.lockedAt ?? null,
        expiresAt: lockDebugStatus?.expiresAt ?? null,
        lastActivityAt: lockDebugStatus?.lastActivityAt ?? null,
        takeoverRequestedAt: lockDebugStatus?.takeoverRequestedAt ?? null,
        pollingMode,
        nextHeartbeatAt,
        nextTakeoverPollAt,
        heartbeatInFlight,
        takeoverWaitInFlight,
      },
      registerTakeoverSaveHandler,
    }),
    [
      getLockedByUserName,
      heartbeatInFlight,
      isLocked,
      lockDebugStatus,
      lockToken,
      lockedByOther,
      lockedByUserName,
      nextHeartbeatAt,
      nextTakeoverPollAt,
      pollingMode,
      registerTakeoverSaveHandler,
      revisionId,
      stage,
      takeoverWaitInFlight,
    ]
  );

  if (stage === 'initializing') {
    return null;
  }

  const shouldRenderChildren = stage === 'ready' || stage === 'being-taken-over';

  return (
    <>
      {shouldRenderChildren && (
        <RevisionLockProviderContext.Provider value={value}>{children}</RevisionLockProviderContext.Provider>
      )}

      {stage === 'blocked-other-user' && (
        <Modal
          opened
          onClose={() => {}}
          title="Revízia je uzamknutá"
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          centered
        >
          <Box mb={32}>
            <P1Regular>
              Táto revízia je momentálne upravovaná používateľom <strong>{lockedByUserName}</strong>. Pre zabránenie
              konfliktom nie je možné ju súčasne upravovať viacerými používateľmi.
            </P1Regular>
          </Box>
          <Group position="right" spacing={16}>
            <Button onClick={handleAcknowledge} data-autofocus>
              Potvrdiť
            </Button>
          </Group>
        </Modal>
      )}

      {stage === 'blocked-other-session' && (
        <Modal
          opened
          onClose={() => {}}
          title="Revízia je otvorená v inom okne"
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          centered
        >
          <Stack spacing={24}>
            <P1Regular>
              Túto revíziu už upravujete v inom okne. Ak chcete pokračovať tu, vyžiadajte prevzatie a počkajte, kým
              pôvodné okno odovzdá vlastníctvo zámku.
            </P1Regular>

            <Group position="right" spacing={16}>
              <Button variant="outline" onClick={handleAcknowledge}>
                Späť
              </Button>
              <Button onClick={handleTakeoverClick} data-autofocus>
                Prevziať v tomto okne
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {stage === 'waiting-for-takeover' && (
        <Modal
          opened
          onClose={() => {}}
          title="Čaká sa na odovzdanie revízie"
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          centered
        >
          <Stack spacing={24}>
            <P1Regular>
              Druhé okno dostalo požiadavku na odovzdanie. Počkáme, kým uloží posledné zmeny a uvoľní zámok, alebo kým
              zámok nevyprší.
            </P1Regular>

            {canForceTakeover && (
              <P1Regular>
                Ak nechcete čakať, môžete revíziu prevziať okamžite. Tento krok môže spôsobiť stratu neuložených zmien v
                pôvodnom okne.
              </P1Regular>
            )}

            <Group position="right" spacing={16}>
              <Button loading>Čakám na uvoľnenie</Button>
              {canForceTakeover && (
                <Button
                  variant="outline"
                  color="orange"
                  onClick={handleForceTakeoverClick}
                  loading={forceTakeoverInFlight}
                >
                  Prevziať okamžite
                </Button>
              )}
            </Group>
          </Stack>
        </Modal>
      )}

      {stage === 'being-taken-over' && (
        <Modal
          opened
          onClose={() => {}}
          title="Revízia sa presúva do iného okna"
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          centered
        >
          <Stack spacing={24}>
            <P1Regular>
              Táto revízia bola otvorená v inom okne. Posledné zmeny sa ešte raz uložia a potom sa toto okno odpojí od
              editácie.
            </P1Regular>

            <Group position="right">
              <Button loading>Ukladám a uvoľňujem zámok</Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </>
  );
}

/**
 * Hook to access the revision lock context.
 */
export function useRevisionLockProvider() {
  const context = useContext(RevisionLockProviderContext);

  if (!context) {
    throw new Error('useRevisionLockProvider must be used within a RevisionLockProvider');
  }

  return context;
}
